import React, { useState, useEffect } from 'react';
import { 
  Input, 
  Button, 
  message,
  Popconfirm,
  Upload,
  Select,
  Spin,
  Alert,
  Space,
  Tooltip,
  Modal
} from 'antd';
import { 
  ExclamationCircleOutlined, 
  UploadOutlined, 
  LoadingOutlined,
  SyncOutlined 
} from '@ant-design/icons';
import AgentConfigService from '../services/AgentConfigService';

const { Option } = Select;

const AgentInputTabs = ({ activeAgent, onSubmit, previousState = null, sessionId }) => {

  // State for active tab
  const [activeTab, setActiveTab] = useState('');
  
  // State for all input values
  const [inputValues, setInputValues] = useState({});
  
  // State for file uploads
  const [fileList, setFileList] = useState({});
  
  // State to track loading state
  const [loading, setLoading] = useState(false);
  
  // State to track validation errors
  const [validationErrors, setValidationErrors] = useState({});
  
  // State for dynamic dropdown values
  const [dynamicDropdownValues, setDynamicDropdownValues] = useState({});
  
  // State for dropdown loading states
  const [dropdownLoading, setDropdownLoading] = useState({});
  
  // State for dropdown cache times (for TTL)
  const [dropdownCacheTimes, setDropdownCacheTimes] = useState({});
  

  const [preProcessModalVisible, setPreProcessModalVisible] = useState(false);
  const [preProcessResults, setPreProcessResults] = useState([]);
  const [preProcessLoading, setPreProcessLoading] = useState(false);


  // Cache TTL - 5 minutes
  const CACHE_TTL = 5 * 60 * 1000;
  
  // Get the primary key field for the current agent and tab
  const getPrimaryKeyField = () => {
    if (!activeAgent || !activeTab) return null;
    
    const inputProcess = activeAgent.input_process?.find(
      process => process.name === activeTab
    );
    
    if (!inputProcess) return null;
    
    // Check both input_parameter and file_parameter
    const parameters = inputProcess.input_parameter || {};
    if (!parameters) return null;
    
    // The first field in the parameters is considered the primary key
    const keys = Object.keys(parameters).filter(key => !key.endsWith('_values'));
    return keys.length > 0 ? keys[0] : null;
  };
  


  
  // Get the primary key value
  const getPrimaryKeyValue = () => {
    const primaryKeyField = getPrimaryKeyField();
    return primaryKeyField ? inputValues[primaryKeyField] : null;
  };
  
  // Check if we're updating an existing workflow
  const isUpdatingExistingWorkflow = () => {
    const primaryKeyField = getPrimaryKeyField();
    const primaryKeyValue = getPrimaryKeyValue();
    
    if (!primaryKeyField || !primaryKeyValue) return false;
    
    return previousState && 
           previousState.primaryKeyField === primaryKeyField && 
           previousState.primaryKeyValue === primaryKeyValue;
  };
  
  // Set first tab as active when agent changes
  useEffect(() => {
    if (activeAgent && activeAgent.input_process && activeAgent.input_process.length > 0) {
      setActiveTab(activeAgent.input_process[0].name);
      
      // Reset input values, file lists and validation errors when agent changes
      // BUT preserve the primary key if resuming previous work
      let initialValues = {};
      
      if (previousState && previousState.primaryKeyField && previousState.primaryKeyValue) {
        initialValues = { [previousState.primaryKeyField]: previousState.primaryKeyValue };
      }
      
      setInputValues(initialValues);
      setFileList({});
      setValidationErrors({});
      // Clear cached dropdown values when agent changes
      setDynamicDropdownValues({});
      setDropdownLoading({});
      setDropdownCacheTimes({});
    }
  }, [activeAgent, previousState]);
  



  // Fetch dynamic dropdown values
  const fetchDynamicDropdownValues = async (fieldKey, endpoint, forceRefresh = false) => {
    if (!activeAgent || !activeAgent.endpoint) return;
    
    // Check cache validity
    const cachedTime = dropdownCacheTimes[fieldKey];
    const now = Date.now();
    const hasValidCache = dynamicDropdownValues[fieldKey] && 
                         dynamicDropdownValues[fieldKey].length > 0 &&
                         cachedTime && 
                         (now - cachedTime) < CACHE_TTL;
    
    if (!forceRefresh && hasValidCache) {
      console.log(`Using cached values for ${fieldKey} (age: ${Math.round((now - cachedTime) / 1000)}s)`);
      return;
    }
    
    // Set loading state
    setDropdownLoading(prev => ({ ...prev, [fieldKey]: true }));
    
    try {
      // Extract base URL from agent endpoint
      const baseUrl = activeAgent.endpoint.split('/').slice(0, 3).join('/');

      console.log('DYNAMIC URL CONTRUCTED   ', baseUrl)
      
      // Remove @ symbol and construct full URL
      let endpointPath = endpoint.substring(1); // Remove @
      
      // Fix common typos (e.g., "proecss" -> "process")
      endpointPath = endpointPath.replace('proecss', 'process');
      
      const fullUrl = `${baseUrl}/${endpointPath}`;
      
      console.log(`Fetching dropdown values for ${fieldKey} from: ${fullUrl}`);
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId || ''}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch dropdown values: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Validate response is an array
      if (Array.isArray(data)) {
        setDynamicDropdownValues(prev => ({ ...prev, [fieldKey]: data }));
        // Store cache time
        setDropdownCacheTimes(prev => ({ ...prev, [fieldKey]: Date.now() }));
      } else if (data && Array.isArray(data.values)) {
        // Handle case where response is {values: [...]}
        setDynamicDropdownValues(prev => ({ ...prev, [fieldKey]: data.values }));
        setDropdownCacheTimes(prev => ({ ...prev, [fieldKey]: Date.now() }));
      } else {
        throw new Error('Invalid response format - expected array');
      }
      
      if (forceRefresh) {
        message.success(`Refreshed ${fieldKey} list`);
      }
      
    } catch (error) {
      console.error(`Error fetching dropdown values for ${fieldKey}:`, error);
      // Set error message as single dropdown option
      setDynamicDropdownValues(prev => ({ 
        ...prev, 
        [fieldKey]: [`Error: ${error.message}`] 
      }));
      message.error(`Failed to load ${fieldKey}: ${error.message}`);
    } finally {
      setDropdownLoading(prev => ({ ...prev, [fieldKey]: false }));
    }
  };
  
  // Fetch dynamic values when tab changes
  useEffect(() => {
    if (!activeAgent || !activeTab) return;
    
    const inputProcess = activeAgent.input_process?.find(
      process => process.name === activeTab
    );
    
    if (!inputProcess) return;
    
    const parameters = inputProcess.input_parameter || inputProcess.file_parameter || {};
    
    // Check for dynamic dropdown fields
    Object.entries(parameters).forEach(([key, value]) => {
      if (key.endsWith('_list_values') && typeof value === 'string' && value.startsWith('@')) {
        const fieldKey = key.replace('_values', ''); // Remove _values to get field key
        // Only fetch if we don't have cached values
        if (!dynamicDropdownValues[fieldKey] || dynamicDropdownValues[fieldKey].length === 0) {
          fetchDynamicDropdownValues(fieldKey, value);
        }
      }
    });
  }, [activeAgent, activeTab]);
  
  // Get input fields for the current tab
const getInputFields = () => {
  if (!activeAgent || !activeTab) return { fields: [], isFileInput: false };
  
  const inputProcess = activeAgent.input_process?.find(
    process => process.name === activeTab
  );
  
  if (!inputProcess) return { fields: [], isFileInput: false };
  
  const fields = [];
  
  // Process input_parameter fields (text, dropdown, password fields)
  if (inputProcess.input_parameter) {
    const inputParams = inputProcess.input_parameter;
    
    Object.entries(inputParams)
      .filter(([key]) => !key.endsWith('_values')) // Filter out dropdown values
      .forEach(([key, label]) => {
        let fieldType = 'text';
        let dropdownValues = null;
        let fieldKey = key;
        let isDynamic = false;
        let dynamicEndpoint = null;
        
        if (key.endsWith('_list')) {
          fieldType = 'dropdown';
          // Look for corresponding _values entry
          const valuesKey = key + '_values';
          const valuesConfig = inputParams[valuesKey];
          
          if (typeof valuesConfig === 'string' && valuesConfig.startsWith('@')) {
            // Use dynamic dropdown values
            isDynamic = true;
            dynamicEndpoint = valuesConfig;
            dropdownValues = dynamicDropdownValues[key] || [];
          } else {
            // Use static dropdown values
            dropdownValues = valuesConfig || [];
          }
        } else if (key.endsWith('_hidden')) {
          fieldType = 'password';
          // Remove _hidden suffix from the key for display
          fieldKey = key.replace('_hidden', '');
        }
        
        fields.push({
          key: fieldKey,
          originalKey: key,
          label,
          type: fieldType,
          dropdownValues,
          isLoading: dropdownLoading[key] || false,
          isDynamic,
          dynamicEndpoint
        });
      });
  }
  
  // Process file_parameter fields (file upload fields)
  if (inputProcess.file_parameter) {
    const fileParams = inputProcess.file_parameter;
    
    Object.entries(fileParams).forEach(([key, label]) => {
      fields.push({
        key: key,
        originalKey: key,
        label,
        type: 'file',
        dropdownValues: null,
        isLoading: false,
        isDynamic: false,
        dynamicEndpoint: null
      });
    });
  }
  
  // Check if ANY file inputs exist
  const hasFileInputs = fields.some(field => field.type === 'file');
  
  return { fields, isFileInput: hasFileInputs };
};
  



  // Handle input change
  const handleInputChange = (key, value) => {
    setInputValues(prevValues => ({
      ...prevValues,
      [key]: value
    }));
    
    // Clear validation error when user types
    if (validationErrors[key]) {
      setValidationErrors(prev => ({
        ...prev,
        [key]: undefined
      }));
    }
  };
  
  // Handle file upload change
  const handleFileChange = (key, info) => {
    const { fileList: newFileList } = info;
    setFileList(prev => ({
      ...prev,
      [key]: newFileList
    }));
    
    // Store the file in inputValues for submission
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj || newFileList[0];
      handleInputChange(key, file);
    } else {
      handleInputChange(key, null);
    }
  };
  
  // Validate form fields
  const validateForm = () => {
    const { fields } = getInputFields();
    const newErrors = {};
    let isValid = true;
    
    // Check each field
    fields.forEach(field => {
      const value = inputValues[field.key];
      
      if (field.type === 'file') {
        // For file inputs, check if a file is selected
        const files = fileList[field.key];
        if (!files || files.length === 0) {
          newErrors[field.key] = `${field.label} is required`;
          isValid = false;
        }
      } else {
        // For other inputs, check if field is blank or undefined
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          newErrors[field.key] = `${field.label} is required`;
          isValid = false;
        }
      }
    });
    
    setValidationErrors(newErrors);
    return isValid;
  };
  
  // Handle submission
  const handleSubmit = async () => {
    if (!activeAgent || !activeTab) {
      message.error('No agent or tab selected');
      return;
    }
    
    // Validate all required fields
    if (!validateForm()) {
      message.error('Please fill in all required fields');
      return;
    }
    
    // Check if we're updating an existing workflow
    const updating = isUpdatingExistingWorkflow();
    
    // Set loading state
    setLoading(true);
    
    // Find the active input process to get the routing_param
    const activeProcess = activeAgent.input_process?.find(
      process => process.name === activeTab
    );
    
    // Get the routing param from the active process
    const routingParam = activeProcess?.routing_param || '';
    // Get the routing param from the active process
    const agent_id = activeProcess?.agent_id || '';

    try {
      // Build the appropriate API URL
      let apiUrl;
      
      if (updating) {
        // If continuing existing work, use continue endpoint
        apiUrl = `${activeAgent.endpoint}/continue`;
      } else {
        // If starting a new workflow, use start endpoint
        apiUrl = `${activeAgent.endpoint}/start`;
      }
      
      if (!apiUrl) {
        throw new Error('Failed to build API URL');
      }
      
      // Get the primary key information to pass to the parent component
      const primaryKeyInfo = {
        primaryKeyField: getPrimaryKeyField(),
        primaryKeyValue: getPrimaryKeyValue(),
        agentId: activeAgent.id
      };
      
      // Prepare submission values - map back to original keys
      const { fields, isFileInput } = getInputFields();
      const submissionValues = {};
      let hasFiles = false;
      
      // Check for files BEFORE mapping to submission values
      fields.forEach(field => {
        if (field.type === 'file' && inputValues[field.key]) {
          hasFiles = true;
        }
      });
      
      // Now map the values
      fields.forEach(field => {
        submissionValues[field.originalKey] = inputValues[field.key];
      });
      
      // Debug logging
      console.log("=== AgentInputTabs Debug ===");
      console.log("Active Agent:", activeAgent);
      console.log("Active Tab:", activeTab);  
      console.log("Active Process:", activeProcess);
      console.log("Agent ID:", activeProcess?.agent_id);
      console.log('agent id', agent_id)
      console.log("hasFiles:", hasFiles);
      console.log("submissionValues:", submissionValues);
      console.log("File fields found:", fields.filter(f => f.type === 'file'));
      console.log("File values:", Object.entries(inputValues).filter(([k, v]) => v instanceof File));
      
      // Call the onSubmit callback with the API URL, values, and primary key info
      // Pass additional flag to indicate if this submission contains files
      await onSubmit(apiUrl, submissionValues, activeTab, primaryKeyInfo, routingParam, hasFiles, agent_id);
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Failed to submit form');
    } finally {
      setLoading(false);
    }
  };
  



  // Check if agent supports pre-processing
  const supportsPreProcess = () => {
    console.log("From supportsPreprocess ", activeAgent)
    return activeAgent?.['pre-process'] === true;
  };



  // Handle pre-process button click
  const handlePreProcess = async () => {
    if (!activeAgent || !activeTab) {
      message.error('No agent or tab selected');
      return;
    }

    // Validate all required fields first
    if (!validateForm()) {
      message.error('Please fill in all required fields');
      return;
    }

    // Show modal and start loading
    setPreProcessModalVisible(true);
    setPreProcessLoading(true);
    setPreProcessResults([]);
    
    try {
      // Find the active input process to get the routing_param and agent_id
      const activeProcess = activeAgent.input_process?.find(
        process => process.name === activeTab
      );
      
      const routingParam = activeProcess?.routing_param || '';
      const agent_id = activeProcess?.agent_id || '';

      // Build API URL
      const apiUrl = `${activeAgent.endpoint}/pre_process`;
      
      // Prepare submission values - map back to original keys
      const { fields } = getInputFields();
      const submissionValues = {};
      
      fields.forEach(field => {
        submissionValues[field.originalKey] = inputValues[field.key];
      });

      const requestBody = {
        ...submissionValues,
        routing_param: routingParam,
        agent_id: agent_id,
        project_id: "11111111-1111-1111-1111-111111111111"
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Pre-process failed: ${response.status} - ${errorText}`);
      }

      const results = await response.json();
      setPreProcessResults(results);

    } catch (error) {
      console.error('Error in pre-process:', error);
      setPreProcessResults([{
        step: "Pre-process Error",
        status: false,
        message: error.message
      }]);
    } finally {
      setPreProcessLoading(false);
    }
  };


  // Add this function to handle modal close
  const handlePreProcessModalClose = () => {
    setPreProcessModalVisible(false);
    setPreProcessResults([]);
    setPreProcessLoading(false);
  };


  // render modal content
  const renderPreProcessModalContent = () => {
    if (preProcessLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', fontSize: '16px' }}>
            Running pre-process checks...
          </div>
        </div>
      );
    }

    if (preProcessResults.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>No results available</div>
        </div>
      );
    }

    const successCount = preProcessResults.filter(r => r.status === true).length;
    const totalCount = preProcessResults.length;
    const hasFailures = successCount < totalCount;

    return (
      <div>
        {/* Summary */}
        <div style={{ 
          marginBottom: '20px', 
          padding: '12px', 
          backgroundColor: hasFailures ? '#fff7e6' : '#f6ffed',
          border: `1px solid ${hasFailures ? '#ffd591' : '#b7eb8f'}`,
          borderRadius: '6px'
        }}>
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: '16px',
            color: hasFailures ? '#fa8c16' : '#52c41a'
          }}>
            {hasFailures ? 
              `${successCount}/${totalCount} checks passed` : 
              `All ${totalCount} checks passed!`
            }
          </div>
        </div>

        {/* Individual Results */}
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {preProcessResults.map((result, index) => (
            <div 
              key={index}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '12px',
                backgroundColor: result.status ? '#f6ffed' : '#fff2f0',
                border: `1px solid ${result.status ? '#b7eb8f' : '#ffccc7'}`,
                borderRadius: '6px'
              }}
            >
              <div style={{
                marginRight: '12px',
                fontSize: '18px',
                color: result.status ? '#52c41a' : '#ff4d4f',
                fontWeight: 'bold',
                lineHeight: '1.2'
              }}>
                {result.status ? '✓' : '✗'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '4px',
                  color: '#262626'
                }}>
                  {result.step}
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#595959',
                  lineHeight: '1.4'
                }}>
                  {result.message}
                </div>
              </div>
            </div>
          ))}
        </Space>
      </div>
    );
  };


  // Render input field based on type
  const renderInputField = (field) => {
    const value = inputValues[field.key] || '';
    const error = validationErrors[field.key];
    
    switch (field.type) {
      case 'file':
        return (
          <div key={field.key} style={{ flex: 1, minWidth: '200px', maxWidth: '350px' }}>
            <Upload
              fileList={fileList[field.key] || []}
              onChange={(info) => handleFileChange(field.key, info)}
              beforeUpload={() => false} // Prevent auto upload
              maxCount={1}
            >
              <Button icon={<UploadOutlined />} style={{ width: '100%' }}>
                {field.label}
              </Button>
            </Upload>
            {error && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );
        
      case 'dropdown':
        return (
          <div key={field.key} style={{ flex: 1, minWidth: '450px', maxWidth: '650px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Select
                  placeholder={field.label}
                  value={value}
                  onChange={(val) => handleInputChange(field.key, val)}
                  status={error ? 'error' : ''}
                  style={{ width: '100%' }}
                  loading={field.isLoading}
                  notFoundContent={field.isLoading ? <Spin size="small" /> : 'No Data'}
                >
                  {field.dropdownValues?.map(val => (
                    <Option key={val} value={val}>{val}</Option>
                  ))}
                </Select>
                {error && (
                  <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                    {error}
                  </div>
                )}
              </div>
              {field.isDynamic && (
                <Tooltip title="Refresh list">
                  <Button
                    icon={<SyncOutlined spin={field.isLoading} />}
                    onClick={() => {
                      if (field.dynamicEndpoint) {
                        fetchDynamicDropdownValues(field.key, field.dynamicEndpoint, true);
                      }
                    }}
                    disabled={field.isLoading}
                    size="small"
                  />
                </Tooltip>
              )}
            </div>
          </div>
        );
        
      case 'password':
        return (
          <div key={field.key} style={{ flex: 1, minWidth: '200px', maxWidth: '350px' }}>
            <Input.Password
              placeholder={field.label}
              value={value}
              onChange={(e) => handleInputChange(field.key, e.target.value)}
              status={error ? 'error' : ''}
            />
            {error && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );
        
      default:
        return (
          <div key={field.key} style={{ flex: 1, minWidth: '200px', maxWidth: '350px' }}>
            <Input
              placeholder={field.label}
              value={value}
              onChange={(e) => handleInputChange(field.key, e.target.value)}
              status={error ? 'error' : ''}
            />
            {error && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );
    }
  };
  
  // No agent selected or agent doesn't have input_process
  if (!activeAgent || !activeAgent.input_process) {
    return null;
  }
  
  // Safely access input_process, ensuring it's an array
  const inputProcesses = Array.isArray(activeAgent.input_process) ? 
    activeAgent.input_process : [];
  
  // Get the input fields for the current tab
  const { fields } = getInputFields();
  
  // Check if changing primary key that could delete existing data
  const isChangingPrimaryKey = () => {
    if (!previousState || !previousState.primaryKeyField || !previousState.primaryKeyValue) {
      return false;
    }
    
    const currentValue = inputValues[previousState.primaryKeyField];
    return currentValue && currentValue !== previousState.primaryKeyValue;
  };
  
  return (
    <div style={{ background: 'white' }}>
      {/* Tabs */}
      <div style={{ 
        background: '#6200EE', 
        display: 'flex', 
        alignItems: 'flex-end',
        padding: '0 16px',
        height: '40px',
        marginBottom: '0',
        borderBottom: '1px solid #6200EE'
      }}>
        {inputProcesses.map(process => (
          <div 
            key={process.name || 'unknown'}
            style={{ 
              padding: '0 16px', 
              height: '36px',
              display: 'flex', 
              alignItems: 'center',
              background: activeTab === process.name ? 'white' : '#d1b1ff',
              color: '#6200EE',
              cursor: 'pointer',
              borderTopLeftRadius: '6px',
              borderTopRightRadius: '6px',
              fontWeight: activeTab === process.name ? 'bold' : 'normal',
              marginRight: '4px',
              position: 'relative',
              top: activeTab === process.name ? '1px' : '0'
            }}
            onClick={() => setActiveTab(process.name)}
          >
            {process.name || 'Tab'}
          </div>
        ))}
      </div>
      
      {/* Input fields section with submit button on the right */}
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          {/* Input fields take up remaining space */}
          <div style={{ 
            display: 'flex', 
            flex: 1, 
            gap: '16px', 
            flexWrap: 'wrap'
          }}>
            {fields.map(field => renderInputField(field))}
          </div>
          
          {/* Submit button fixed at the right */}
          <div style={{ width: '200px', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
              {/* Pre-Process Button */}
              {supportsPreProcess() && (
                <Button 
                  type="default"
                  loading={preProcessLoading}
                  style={{ 
                    borderColor: '#6200EE',
                    color: '#6200EE',
                    width: '100%'
                  }}
                  onClick={handlePreProcess}
                >
                  Pre-Process
                </Button>
              )}
              
              {/* Submit Button */}
              {isChangingPrimaryKey() ? (
                <Popconfirm
                  title="Restart process"
                  description="Starting a new workflow will delete all data associated with the current process. Are you sure you want to continue?"
                  onConfirm={handleSubmit}
                  okText="Yes"
                  cancelText="No"
                  icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                >
                  <Button 
                    type="primary" 
                    loading={loading}
                    style={{ 
                      background: '#6200EE',
                      width: '100%'
                    }}
                  >
                    Submit
                  </Button>
                </Popconfirm>
              ) : (
                <Button 
                  type="primary" 
                  loading={loading}
                  style={{ 
                    background: '#6200EE',
                    width: '100%'
                  }} 
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              )}
            </div>
          </div>

        </div>
      </div>

      <Modal
        title="Pre-Process Results"
        open={preProcessModalVisible}
        onCancel={handlePreProcessModalClose}
        footer={preProcessLoading ? null : [
          <Button key="close" onClick={handlePreProcessModalClose}>
            Close
          </Button>
        ]}
        width={600}
        centered
        maskClosable={!preProcessLoading}
        closable={!preProcessLoading}
      >
        {renderPreProcessModalContent()}
      </Modal>

    </div>
  );
};

export default AgentInputTabs;